import React, { useState, useEffect } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useSession } from '@/lib/SessionContext';
import AsaNotificationManager from '@/components/asa/AsaNotificationManager';
import AutoPOCompleter from '@/components/production/AutoPOCompleter';
import { BirthdayProvider } from '@/contexts/BirthdayContext';
import LateAbsencePopup from '@/components/attendance/LateAbsencePopup';
import TestingModeBanner from '@/components/shared/TestingModeBanner';
import VersionUpdatePrompt from '@/components/shared/VersionUpdatePrompt';
import {
  Menu, X, ChevronRight, ChevronLeft, ChevronDown, Clock, LogOut, User,
  Building2, LayoutDashboard, Users, UserCheck, Settings, DollarSign,
  Receipt, Calculator, Coins, TrendingUp, PackageSearch, Layers, FlaskConical,
  History, Database, Truck, CheckCircle2, RotateCcw, BarChart3, ClipboardList, TrendingDown, Bot, Trash2, PackageOpen, Box, Layout, Activity, ShoppingCart, Wallet, HardDrive, Banknote, AlertTriangle, PieChart, Trophy, Library, Archive, Bell, ShieldCheck
} from 'lucide-react';
import TrashBin from '@/components/common/TrashBin';
import NotificationBell from './NotificationBell';
import LiveClock from './LiveClock';
import MobileBottomNav from './MobileBottomNav';
import ClockInButton from './ClockInButton';
import { Button } from '@/components/ui/button';
import { checkIsDev, checkIsOwner, getPageAccess } from '@/lib/AuthSession';

// Helper function to define mobile bottom nav items (max 5) per portal
export const getMobileNavItems = (pathname) => {
  if (pathname.startsWith('/office/workspace')) {
    return [
      { path: '/office', label: 'Hub', icon: Building2 },
      { path: '/office/workspace/clients', label: 'Klien', icon: Users },
      { path: '/office/workspace', label: 'Home', icon: Layout },
      { path: '/office/workspace/team', label: 'Tim', icon: UserCheck },
      { path: '/office/workspace/settings', label: 'Setelan', icon: Settings },
    ];
  }
  if (pathname.startsWith('/office/finance')) {
    return [
      { path: '/office/finance/invoices', label: 'Tagihan', icon: Receipt },
      { path: '/office/finance/accounts', label: 'Kas', icon: Coins },
      { path: '/office/finance', label: 'Home', icon: DollarSign },
      { path: '/office/finance/reports', label: 'Laporan', icon: TrendingUp },
      { path: '/office/finance/profile', label: 'Profil', icon: User },
    ];
  }
  if (pathname.startsWith('/office/marketing')) {
    return [
      { path: '/office/marketing/client-data', label: 'Klien', icon: Users },
      { path: '/office/marketing/calculator', label: 'Invoice', icon: Calculator },
      { path: '/office/marketing', label: 'Home', icon: TrendingUp },
      { path: '/office/marketing/tasks-report', label: 'Tugas', icon: ClipboardList },
      { path: '/office/marketing/profile', label: 'Profil', icon: User },
    ];
  }
  if (pathname.startsWith('/office/purchasing')) {
    return [
      { path: '/office/purchasing/suppliers', label: 'Supplier', icon: Database },
      { path: '/office/purchasing/data-pemesan', label: 'Data Pemesan', icon: Building2 },
      { path: '/office/purchasing/items-to-buy', label: 'Belanja', icon: ShoppingCart },
      { path: '/office/purchasing', label: 'Home', icon: LayoutDashboard },
      { path: '/office/purchasing/delivery', label: 'Kirim', icon: Truck },
      { path: '/office/purchasing/profile', label: 'Profil', icon: User },
    ];
  }
  if (pathname.startsWith('/office/analytics')) {
    return [
      { path: '/office/analytics/ranking', label: 'Ranking', icon: Trophy },
      { path: '/office/analytics/margin-sample', label: 'Margin Sample', icon: PieChart },
      { path: '/office/analytics', label: 'Dashboard', icon: TrendingUp },
      { path: '/office/analytics/margin-po', label: 'Margin PO', icon: TrendingUp },
      { path: '/office/workspace', label: 'Hub', icon: Building2 },
    ];
  }
  // Default: Production
  return [
    { path: '/office/production/po-list', label: 'PO', icon: Building2 },
    { path: '/office/production/tasks', label: 'Tugas', icon: ClipboardList },
    { path: '/office/production', label: 'Home', icon: LayoutDashboard },
    { path: '/office/production/delivery', label: 'Kirim', icon: Truck },
    { path: '/office/production/profile', label: 'Profil', icon: User },
  ];
};

export const getPortalName = (pathname) => {
  if (pathname.startsWith('/office/workspace')) return 'Workspace';
  if (pathname.startsWith('/office/finance')) return 'Finance';
  if (pathname.startsWith('/office/marketing')) return 'Marketing';
  if (pathname.startsWith('/office/purchasing')) return 'Purchasing';
  if (pathname.startsWith('/office/analytics')) return 'Analytics Management';
  return 'Produksi';
};

export const getSidebarNav = (member, pathname) => {
  let nav = [];

  // 1. WORKSPACE PORTAL MENU
  if (pathname.startsWith('/office/workspace')) {
    nav = [
      { group: 'NAVIGASI PORTAL', items: [
        { path: '/office', label: 'Hub Kantor Pusat', icon: Building2 },
      ]},
      { group: 'WORKSPACE ADMIN', items: [
        { path: '/office/workspace', label: 'Dashboard Workspace', icon: Layout, accessKey: 'dashboard_workspace' },
        { path: '/office/workspace/clients', label: 'Klien Terverifikasi', icon: Users, accessKey: 'klien_terverifikasi' },
        { path: '/office/workspace/absensi', label: 'Absensi (ADMS)', icon: Clock, accessKey: 'absensi' },
        { path: '/office/workspace/simulasi-biaya', label: 'Simulasi Biaya Produksi', icon: FlaskConical, accessKey: 'simulasi_biaya' },
      ]},
      { group: 'MANAJEMEN TIM & AKSES', items: [
        { path: '/office/workspace/team', label: 'Pengguna & Role', icon: UserCheck, accessKey: 'manajemen_tim' },
        { path: '/office/workspace/access', label: 'Hak Akses & Notifikasi', icon: ShieldCheck, accessKey: 'pengaturan_akses' },
        { path: '/office/workspace/activity-log', label: 'Log Aktivitas', icon: History, accessKey: 'log_aktivitas' },
        { path: '/office/workspace/settings', label: 'Pengaturan Perusahaan', icon: Settings, accessKey: 'pengaturan_perusahaan' },
      ]},
      { group: 'DOKUMEN & STANDAR', items: [
        { path: '/office/workspace/batch-database', label: 'Database No. Batch', icon: Layers, accessKey: 'batch_database' },
        { path: '/office/workspace/dip-generator', label: 'DIP Generator', icon: ClipboardList, accessKey: 'dip_generator' },
      ]},
      { group: 'INTELLIGENCE', items: [
        { path: '/office/workspace/ai', label: 'ASA (AI Analyst)', icon: Bot, accessKey: 'workspace_ai' },
        { path: '/office/workspace/health', label: 'Healthy Web', icon: Activity, accessKey: 'workspace_health' },
        { path: '/office/workspace/backup', label: 'Backup & Restore', icon: HardDrive, accessKey: 'workspace_backup' },
        { path: '/office/workspace/history', label: 'Gudang Arsip (History)', icon: Archive, accessKey: 'workspace_history' },
      ]}
    ];
  }

  // 2. FINANCE PORTAL MENU
  else if (pathname.startsWith('/office/finance')) {
    nav = [
      { group: 'NAVIGASI PORTAL', items: [
        { path: '/office', label: 'Hub Kantor Pusat', icon: Building2 },
      ]},
      { group: 'UTAMA', items: [
        { path: '/office/finance', label: 'Dashboard Finance', icon: DollarSign, accessKey: 'dashboard_finance' },
      ]},
      { group: 'KAS & BANK', items: [
        { path: '/office/finance/accounts', label: 'Rekening & Mutasi', icon: Coins, accessKey: 'rekening_mutasi' },
      ]},
      { group: 'PENJUALAN & PEMBELIAN', items: [
        { path: '/office/finance/calculator', label: 'Pembuatan Invoice & SPH', icon: Calculator, accessKey: 'finance_calculator' },
        { path: '/office/finance/sph-list', label: 'Daftar SPH', icon: Receipt, accessKey: 'finance_sph_list' },
        { path: '/office/finance/invoices', label: 'Invoice & PO', icon: Receipt, accessKey: 'invoice_po' },
        { path: '/office/finance/custom-invoices', label: 'Invoice Custom & Sample', icon: Receipt, accessKey: 'finance_custom_invoices' },
        { path: '/office/finance/payables', label: 'Hutang Perusahaan', icon: Receipt, accessKey: 'finance_payables' },
        { path: '/office/finance/receivables', label: 'Piutang Klien', icon: Receipt, accessKey: 'finance_receivables' },
        { path: '/office/finance/purchasing-requests', label: 'Pengajuan Pembelian', icon: Wallet, accessKey: 'finance_purchasing_requests' },
      ]},
      { group: 'BIAYA', items: [
        { path: '/office/finance/production-costs', label: 'Biaya Produksi', icon: FlaskConical, accessKey: 'biaya_produksi' },
        { path: '/office/finance/operational', label: 'Biaya Operasional', icon: TrendingDown, accessKey: 'biaya_operasional' },
        { path: '/office/finance/payroll', label: 'Gaji & Karyawan', icon: Users, accessKey: 'gaji_karyawan' },
        { path: '/office/finance/laporan', label: 'Rekap Laporan Biaya', icon: BarChart3, accessKey: 'finance_laporan' },
      ]},
      { group: 'ASET & INVENTORI', items: [
        { path: '/office/finance/inventory', label: 'Stok & Inventori', icon: PackageOpen, accessKey: 'stok_inventori' },
        { path: '/office/finance/assets', label: 'Aset Perusahaan', icon: Box, accessKey: 'finance_assets' },
      ]},
      { group: 'AKUNTANSI & LAPORAN', items: [
        { path: '/office/finance/chart-of-accounts', label: 'Daftar Akun (COA)', icon: Database, accessKey: 'finance_coa' },
        { path: '/office/finance/reports-hub', label: 'Laporan Keuangan', icon: BarChart3, accessKey: 'finance_reports_hub' },
        { path: '/office/finance/laporan-ppn', label: 'Laporan PPN', icon: Receipt, accessKey: 'finance_laporan_ppn' },
        { path: '/office/finance/reject-tracking', label: 'Laporan Reject', icon: AlertTriangle, accessKey: 'finance_reject_tracking' },
      ]},
      { group: 'INTELLIGENCE', items: [
        { path: '/office/finance/ai', label: 'ASA (AI Analyst)', icon: Bot, accessKey: 'finance_ai' },
      ]}
    ];
  }

  // 3. MARKETING PORTAL MENU
  else if (pathname.startsWith('/office/marketing')) {
    nav = [
      { group: 'NAVIGASI PORTAL', items: [
        { path: '/office', label: 'Hub Kantor Pusat', icon: Building2 },
      ]},
      { group: 'MARKETING', items: [
        { path: '/office/marketing', label: 'Dashboard Marketing', icon: TrendingUp, accessKey: 'dashboard_marketing' },
        { path: '/office/marketing/ranking', label: 'Ranking Omset', icon: Trophy, accessKey: 'ranking_omset' },
        { path: '/office/marketing/client-data', label: 'Data Klien', icon: Users, accessKey: 'data_klien' },
        { path: '/office/marketing/sample', label: 'Sample Racikan', icon: FlaskConical, accessKey: 'sample_racikan' },
        { path: '/office/marketing/profit-sample', label: 'Margin Sample', icon: PieChart, accessKey: 'marketing_profit_sample' },
        { path: '/office/marketing/brands', label: 'Database Brand', icon: Database, accessKey: 'laporan_brand' },
      ]},
      { group: 'TAGIHAN & INVOICE', items: [
        { path: '/office/marketing/calculator', label: 'Pembuatan Invoice & SPH', icon: Calculator, accessKey: 'marketing_calculator' },
        { path: '/office/marketing/sph-list', label: 'Daftar SPH', icon: Receipt, accessKey: 'marketing_sph_list' },
        { path: '/office/marketing/invoices', label: 'Invoice & PO', icon: Receipt, accessKey: 'marketing_invoices' },
        { path: '/office/marketing/custom-invoices', label: 'Invoice Custom & Sample', icon: Receipt, accessKey: 'marketing_custom_invoices' },
        { path: '/office/marketing/receivables', label: 'Piutang Klien', icon: Wallet, accessKey: 'marketing_receivables' },
      ]},
      { group: 'PRODUKSI', items: [
        { path: '/office/marketing/tasks-report', label: 'Tugas & Waktu Produksi', icon: ClipboardList, accessKey: 'tugas_waktu_produksi' },
      ]},
      { group: 'INVENTORI & STOK', items: [
        { path: '/office/marketing/inventory', label: 'Stok & Inventori', icon: PackageOpen, accessKey: 'marketing_inventory' },
      ]},
      { group: 'INTELLIGENCE', items: [
        { path: '/office/marketing/ai', label: 'ASA (AI Analyst)', icon: Bot, accessKey: 'marketing_ai' },
      ]}
    ];
  }

  // 4. PURCHASING PORTAL MENU
  else if (pathname.startsWith('/office/purchasing')) {
    nav = [
      { group: 'NAVIGASI PORTAL', items: [
        { path: '/office', label: 'Hub Kantor Pusat', icon: Building2 },
      ]},
      { group: 'PURCHASING', items: [
        { path: '/office/purchasing', label: 'Dashboard Purchasing', icon: LayoutDashboard, accessKey: 'dashboard_purchasing' },
      ]},
      { group: 'TRANSAKSI & LAPORAN', items: [
        { path: '/office/purchasing/low-stock', label: 'Item Perlu Beli', icon: AlertTriangle, accessKey: 'barang_harus_dibeli' },
        { path: '/office/purchasing/items-to-buy', label: 'Item Dibutuhkan PO', icon: ShoppingCart, accessKey: 'purchasing_items_to_buy' },
        { path: '/office/purchasing/items-po', label: 'Item Sudah PO', icon: ShoppingCart, accessKey: 'purchasing_items_po' },
        { path: '/office/purchasing/purchasing-requests', label: 'Pengajuan Pembelian', icon: Wallet, accessKey: 'purchasing_requests' },
        { path: '/office/purchasing/payables', label: 'Hutang Perusahaan', icon: Banknote, accessKey: 'purchasing_payables' },
        { path: '/office/purchasing/laporan-ppn', label: 'Laporan PPN', icon: Receipt, accessKey: 'purchasing_laporan_ppn' },
      ]},
      { group: 'DATABASE & LIBRARY', items: [
        { path: '/office/purchasing/client-data', label: 'Data Klien', icon: Users, accessKey: 'purchasing_client_data' },
        { path: '/office/purchasing/suppliers', label: 'Database Supplier', icon: Database, accessKey: 'database_supplier' },
        { path: '/office/purchasing/data-pemesan', label: 'Data Pemesan', icon: Building2, accessKey: 'purchasing_data_pemesan' },
        { path: '/office/purchasing/documents', label: 'Library Dokumen', icon: Library, accessKey: 'purchasing_documents' },
      ]},
      { group: 'INVENTORI & STOK', items: [
        { path: '/office/purchasing/inventory', label: 'Stok & Inventori', icon: PackageOpen, accessKey: 'purchasing_inventory' },
      ]},
      { group: 'DELIVERY & LOGISTIK', items: [
        { path: '/office/purchasing/delivery', label: 'Surat Jalan & Pengiriman', icon: Truck, accessKey: 'purchasing_delivery' },
      ]},
      { group: 'INTELLIGENCE', items: [
        { path: '/office/purchasing/ai', label: 'ASA (AI Analyst)', icon: Bot, accessKey: 'purchasing_ai' },
      ]}
    ];
  }

  // 5. ANALYTICS PORTAL MENU
  else if (pathname.startsWith('/office/analytics')) {
    nav = [
      { group: 'NAVIGASI PORTAL', items: [
        { path: '/office', label: 'Hub Kantor Pusat', icon: Building2 },
      ]},
      { group: 'ANALYTICS MANAGEMENT', items: [
        { path: '/office/analytics', label: 'Dashboard Analytics', icon: TrendingUp, accessKey: 'dashboard_analytics' },
        { path: '/office/analytics/margin-keseluruhan', label: 'Margin Keseluruhan', icon: Layers, accessKey: 'analytics_margin_keseluruhan' },
        { path: '/office/analytics/laporan-keuangan', label: 'Laporan Keuangan', icon: DollarSign, accessKey: 'analytics_laporan_keuangan' },
        { path: '/office/analytics/margin-po', label: 'Margin PO', icon: DollarSign, accessKey: 'analytics_margin_po' },
        { path: '/office/analytics/margin-sample', label: 'Margin Sample', icon: PieChart, accessKey: 'analytics_margin_sample' },
        { path: '/office/analytics/margin-custom', label: 'Margin Custom', icon: PieChart, accessKey: 'analytics_margin_custom' },
        { path: '/office/analytics/ranking', label: 'Ranking Omset', icon: Trophy, accessKey: 'analytics_ranking' },
        { path: '/office/analytics/omset', label: 'Omset', icon: TrendingUp, accessKey: 'analytics_omset' },
        { path: '/office/analytics/laporan-ppn', label: 'Laporan PPN', icon: Receipt, accessKey: 'analytics_laporan_ppn' },
        { path: '/office/analytics/payables-receivables', label: 'Hutang & Piutang', icon: Receipt, accessKey: 'analytics_payables_receivables' },
      ]},
      { group: 'INTELLIGENCE', items: [
        { path: '/office/analytics/ai', label: 'ASA (AI Analyst)', icon: Bot, accessKey: 'analytics_ai' },
      ]}
    ];
  }

  // 6. PRODUCTION PORTAL MENU (Default)
  else {
    nav = [
      { group: 'NAVIGASI PORTAL', items: [
        { path: '/office', label: 'Hub Kantor Pusat', icon: Building2 },
      ]},
      { group: 'UTAMA', items: [
        { path: '/office/production', label: 'Dashboard Utama', icon: LayoutDashboard, accessKey: 'dashboard_produksi' },
        { path: '/office/production/po-list', label: 'Daftar PO Produksi', icon: Building2, accessKey: 'po_produksi' },
        { path: '/office/production/sample', label: 'Sample', icon: FlaskConical, accessKey: 'sample_racikan' },
        { path: '/office/production/brands', label: 'Database Brand', icon: Database, accessKey: 'database_brand' },
      ]},
      { group: 'INVENTORI & STOK', items: [
        { path: '/office/production/inventory', label: 'Stok & Inventori', icon: PackageOpen, accessKey: 'stok_inventori' },
      ]},
      { group: 'DELIVERY', items: [
        { path: '/office/production/delivery', label: 'Delivery', icon: Truck, accessKey: 'delivery' },
        { path: '/office/production/return', label: 'Return', icon: RotateCcw, accessKey: 'return' },
      ]},
      { group: 'LAPORAN', items: [
        { path: '/office/production/reports', label: 'Laporan', icon: BarChart3, accessKey: 'produksi_laporan' },
        { path: '/office/production/reject-tracking', label: 'Laporan Reject', icon: AlertTriangle, accessKey: 'produksi_reject' },
      ]},
      { group: 'TIM PRODUKSI', items: [
        { path: '/office/production/tasks', label: 'Tugas Saya', icon: ClipboardList, accessKey: 'tugas_saya' },
      ]},
      { group: 'INTELLIGENCE', items: [
        { path: '/office/production/ai', label: 'ASA (AI Analyst)', icon: Bot, accessKey: 'produksi_ai' },
      ]}
    ];
  }

  // If no member provided (e.g. WorkspacePageAccess / PageAccessDialog loading full template), return all
  if (!member) return nav;

  // Developer and Owner always have full access to everything
  if (checkIsDev(member) || checkIsOwner(member)) return nav;

  // Filter items for regular staff based on page_access
  return nav
    .map(group => ({
      ...group,
      items: group.items.filter(item => {
        // Items without accessKey (like Hub Kantor Pusat) are always accessible
        if (!item.accessKey) return true;
        // Check access level
        const access = getPageAccess(member, item.accessKey);
        return access !== 'none';
      })
    }))
    .filter(group => group.items.length > 0);
};

function SidebarContent({ onLinkClick, collapsed, onOpenTrash, isMobile }) {
  const location = useLocation();
  const { member, logout } = useSession();
  const navigate = useNavigate();
  
  const navGroups = getSidebarNav(member, location.pathname);
  
  // Track open/closed state of dropdown menus
  const [openDropdowns, setOpenDropdowns] = useState({ team_management_dropdown: true });

  const toggleDropdown = (id) => {
    setOpenDropdowns(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const isActive = (path) => {
    if (path === '/office/workspace' || path === '/office/finance' || path === '/office/production' || path === '/office/marketing' || path === '/office/purchasing') {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  const getRoleLabel = (r) => {
    if (r === 'developer') return 'Developer';
    if (r === 'finance') return 'Finance';
    if (r === 'owner') return 'Owner';
    if (r === 'marketing') return 'Marketing';
    return 'Staff Produksi';
  };

  const getBrandHome = () => {
    if (location.pathname.startsWith('/office/workspace')) return '/office/workspace';
    if (location.pathname.startsWith('/office/finance')) return '/office/finance';
    if (location.pathname.startsWith('/office/marketing')) return '/office/marketing';
    if (location.pathname.startsWith('/office/purchasing')) return '/office/purchasing';
    if (location.pathname.startsWith('/office/analytics')) return '/office/analytics';
    return '/office/production';
  };

  return (
    <div className="flex flex-col h-full bg-[#FFFDF9] border-r border-[#E6D5B8]">
      
      {/* 1. BRAND HEADER */}
      <Link to={getBrandHome()} className="block cursor-pointer">
        <div className={`flex items-center gap-2 md:gap-3 ${collapsed ? 'px-2 py-5 justify-center' : (isMobile ? 'px-3 py-4' : 'px-5 py-5')} border-b border-[#E6D5B8]/40`}>
          <div className={`${isMobile ? 'w-6 h-6' : 'w-8 h-8'} flex items-center justify-center shrink-0`}>
            <img src="/logo.png" alt="Satala Logo" className="w-full h-full object-contain drop-shadow-sm" />
          </div>
          {!collapsed && (
            <div className="flex flex-col">
              <span className={`${isMobile ? 'text-xs' : 'text-sm'} font-black text-[#302621] leading-tight tracking-tight`}>Satala {getPortalName(location.pathname)}</span>
              <span className={`${isMobile ? 'text-[6px]' : 'text-[8px]'} text-[#8C6208] font-bold tracking-widest uppercase mt-0.5 leading-none`}>DERMATECH ESSENTIAL</span>
            </div>
          )}
        </div>
      </Link>
 
      {/* 2. USER PROFILE WIDGET */}
      {!collapsed && (
        <div className={`${isMobile ? 'px-3 py-3' : 'px-4 py-4'} border-b border-[#E6D5B8]/25 bg-slate-50/40`}>
          <div className="flex items-center gap-3">
            <div className={`relative ${isMobile ? 'w-7 h-7 text-xs' : 'w-9 h-9 text-sm'} rounded-full bg-orange-100 border border-orange-200 flex items-center justify-center text-[#8C6208] font-bold shrink-0 shadow-inner overflow-hidden`}>
              {member?.photo_url ? (
                <img src={member.photo_url} alt="" className="w-full h-full object-cover" />
              ) : (
                member?.full_name?.charAt(0) || 'D'
              )}
            </div>
            <div className="min-w-0">
              <h4 className={`${isMobile ? 'text-[10px]' : 'text-xs'} font-black text-[#302621] truncate leading-tight`}>{member?.full_name || 'Developer Satala'}</h4>
              <span className={`inline-block bg-[#F9F5EC] text-[#8C6208] ${isMobile ? 'text-[7px] px-1' : 'text-[9px] px-1.5'} py-0.5 rounded font-black mt-1 uppercase border border-[#E6D5B8]/40`}>
                {member?.position || getRoleLabel(member?.role)}
              </span>
            </div>
          </div>
        </div>
      )}
 
      {/* 3. SIDEBAR NAVIGATION */}
      <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-4">
        {navGroups.map((group) => (
          <div key={group.group}>
            {!collapsed && (
              <p className={`px-3 mb-1.5 ${isMobile ? 'text-[7px]' : 'text-[9px]'} font-black uppercase tracking-wider text-[#A39171] select-none opacity-85`}>
                {group.group}
              </p>
            )}
            {group.items.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path);
              return (
                <Link key={item.path} to={item.path} onClick={onLinkClick} title={collapsed ? item.label : undefined}>
                  <div className={`flex items-center gap-2 md:gap-3 ${isMobile ? 'px-2 py-2' : 'px-3 py-2.5'} rounded-xl mb-1.5 transition-all duration-300 group ${
                    collapsed ? 'justify-center px-2' : ''
                  } ${
                    active 
                      ? 'bg-[#F9F5EC] text-[#8C6208] font-bold border border-[#E6D5B8]/50 shadow-xs' 
                      : 'text-[#7A695B] hover:bg-[#F9F5EC]/40 hover:text-[#8C6208] border border-transparent'
                  }`}>
                    <div className={`${isMobile ? 'w-6 h-6' : 'w-8 h-8'} rounded-lg flex items-center justify-center transition-all duration-300 ${
                      active ? 'text-[#8C6208]' : 'text-[#A39171] group-hover:text-[#8C6208]'
                    }`}>
                      <Icon strokeWidth={1.5} className={`${isMobile ? 'w-4 h-4' : 'w-5 h-5'} shrink-0 transition-transform duration-300 group-hover:scale-110`} />
                    </div>
                    {!collapsed && <span className={`${isMobile ? 'text-xs' : 'text-sm'} font-medium tracking-tight`}>{item.label}</span>}
                  </div>
                </Link>
              );
            })}
          </div>
        ))}
      </nav>

      {/* Trash Bin Button - Hidden in Finance */}
      {!location.pathname.startsWith('/office/finance') && (
        <div className="px-3 pb-1">
          <button
            onClick={() => onOpenTrash?.()}
            title={collapsed ? 'Keranjang Sampah' : undefined}
            className={`w-full flex items-center gap-2 md:gap-3 ${isMobile ? 'px-2 py-2 text-xs' : 'px-3 py-2.5 text-sm'} font-medium text-[#7A695B] hover:bg-[#F9F5EC]/40 hover:text-[#8C6208] transition-colors group ${collapsed ? 'justify-center px-2' : ''}`}
          >
            <div className={`${isMobile ? 'w-6 h-6' : 'w-8 h-8'} rounded-lg flex items-center justify-center text-[#A39171] group-hover:text-[#8C6208] transition-all duration-300`}>
              <Trash2 strokeWidth={1.5} className={`${isMobile ? 'w-4 h-4' : 'w-5 h-5'} shrink-0 transition-transform duration-300 group-hover:scale-110`} />
            </div>
            {!collapsed && <span>Keranjang Sampah</span>}
          </button>
        </div>
      )}
 
      {/* 4. BOTTOM ACTION SYSTEM */}
      <div className={`border-t border-[#E6D5B8]/40 ${isMobile ? 'p-2' : 'p-3'} space-y-1 bg-[#FFFDF9]`}>
        <Link to={location.pathname.startsWith('/office/workspace') ? '/office/workspace/profile' : location.pathname.startsWith('/office/finance') ? '/office/finance/profile' : location.pathname.startsWith('/office/marketing') ? '/office/marketing/profile' : location.pathname.startsWith('/office/purchasing') ? '/office/purchasing/profile' : '/office/production/profile'} onClick={onLinkClick}>
          <div className={`flex items-center gap-2 md:gap-3 ${isMobile ? 'px-2 py-2 text-xs' : 'px-3 py-2.5 text-sm'} font-medium text-[#7A695B] hover:bg-[#F9F5EC]/40 hover:text-[#8C6208] group ${collapsed ? 'justify-center px-2' : ''}`}>
            <div className={`${isMobile ? 'w-6 h-6' : 'w-8 h-8'} rounded-lg flex items-center justify-center text-[#A39171] group-hover:text-[#8C6208] transition-all duration-300`}>
              <User strokeWidth={1.5} className={`${isMobile ? 'w-4 h-4' : 'w-5 h-5'} shrink-0 transition-transform duration-300 group-hover:scale-110`} />
            </div>
            {!collapsed && <span>Profil Saya</span>}
          </div>
        </Link>
        <button
          onClick={() => { logout?.(); navigate('/login-tim'); }}
          className={`w-full flex items-center gap-2 md:gap-3 ${isMobile ? 'px-2 py-2 text-xs' : 'px-3 py-2.5 text-sm'} font-medium text-[#7A695B] hover:bg-[#F9F5EC]/40 hover:text-[#8C6208] transition-colors group ${collapsed ? 'justify-center px-2' : ''}`}
        >
          <div className={`${isMobile ? 'w-6 h-6' : 'w-8 h-8'} rounded-lg flex items-center justify-center text-[#A39171] group-hover:text-[#8C6208] transition-all duration-300`}>
            <LogOut strokeWidth={1.5} className={`${isMobile ? 'w-4 h-4' : 'w-5 h-5'} shrink-0 transition-transform duration-300 group-hover:scale-110`} />
          </div>
          {!collapsed && <span>Keluar</span>}
        </button>
      </div>

    </div>
  );
}

export default function AppLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [showTrash, setShowTrash] = useState(false);
  const { member } = useSession();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Real-time Indonesian Live Clock State
  const [clockTime, setClockTime] = useState('');
  const [clockDate, setClockDate] = useState('');

  useEffect(() => {
    const updateTime = () => {
      const days = ['Min', 'Sen', 'Sel', 'Rab', 'Kam', 'Jum', 'Sab'];
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'Mei', 'Jun', 'Jul', 'Agt', 'Sep', 'Okt', 'Nov', 'Des'];
      
      const now = new Date();
      const day = days[now.getDay()];
      const date = now.getDate();
      const month = months[now.getMonth()];
      const year = now.getFullYear();
      
      const hours = String(now.getHours()).padStart(2, '0');
      const minutes = String(now.getMinutes()).padStart(2, '0');
      const seconds = String(now.getSeconds()).padStart(2, '0');
      
      setClockTime(`${hours}.${minutes}.${seconds} WIB`);
      setClockDate(`${day}, ${date} ${month} ${year}`);
    };

    updateTime();
    const interval = setInterval(updateTime, 1000);
    return () => clearInterval(interval);
  }, []);

  // Route Access Guard for subpages based on page_access
  useEffect(() => {
    if (!member) return;
    if (checkIsDev(member) || checkIsOwner(member)) return;

    // Get full unfiltered navigation for current portal
    const fullNav = getSidebarNav(null, location.pathname);
    const allItems = fullNav.flatMap(g => g.items);
    
    // Find item matching current path
    const currentItem = allItems.find(it => {
      if (it.path === location.pathname) return true;
      if (it.path !== '/office' && it.path !== '/office/workspace' && it.path !== '/office/finance' && it.path !== '/office/production' && it.path !== '/office/marketing' && it.path !== '/office/purchasing' && it.path !== '/office/analytics') {
        return location.pathname.startsWith(it.path);
      }
      return false;
    });

    if (currentItem && currentItem.accessKey) {
      const access = getPageAccess(member, currentItem.accessKey);
      if (access === 'none') {
        // User has NO access to this specific page!
        // Find the first accessible page in this portal for this user
        const allowedNav = getSidebarNav(member, location.pathname);
        const allowedItems = allowedNav.flatMap(g => g.items).filter(it => it.path !== '/office' && it.path !== location.pathname);
        if (allowedItems.length > 0) {
          navigate(allowedItems[0].path, { replace: true });
        } else {
          navigate('/office', { replace: true });
        }
      }
    }
  }, [member, location.pathname, navigate]);

  if (!member) return null;

  const sidebarWidth = sidebarCollapsed ? 'w-20' : 'w-64';
  const mainMargin = sidebarCollapsed ? 'md:ml-20' : 'md:ml-64';

  return (
    <BirthdayProvider>
    <div className="min-h-screen print:min-h-0 bg-[#FCFAF5] flex font-sans antialiased text-[#302621] print:bg-white print:block">
      
      {/* Desktop Sidebar */}
      <aside className={`hidden md:flex ${sidebarWidth} shrink-0 flex-col bg-[#FFFDF9] fixed inset-y-0 left-0 z-30 transition-all duration-300 shadow-xs print:hidden`}>
        <SidebarContent collapsed={sidebarCollapsed} onOpenTrash={() => setShowTrash(true)} />
        {/* Toggle collapse button */}
        <button
          onClick={() => setSidebarCollapsed((p) => !p)}
          className="absolute -right-3 top-16 w-6 h-6 rounded-full bg-white border border-[#E6D5B8]/80 flex items-center justify-center shadow-xs hover:bg-[#F9F5EC] text-[#8C6208] transition-colors z-40 print:hidden"
        >
          {sidebarCollapsed ? <ChevronRight className="w-3.5 h-3.5" /> : <ChevronLeft className="w-3.5 h-3.5" />}
        </button>
      </aside>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <>
          <div className="fixed inset-0 z-[80] bg-[#302621]/30  md:hidden print:hidden" onClick={() => setSidebarOpen(false)} />
          <aside className="fixed inset-y-0 left-0 z-[90] w-[180px] bg-[#FFFDF9] md:hidden flex flex-col shadow-2xl print:hidden">
            <div className="flex-1 overflow-hidden">
              <SidebarContent onLinkClick={() => setSidebarOpen(false)} collapsed={false} isMobile={true} onOpenTrash={() => { setSidebarOpen(false); setShowTrash(true); }} />
            </div>
          </aside>
        </>
      )}

      {/* Main Content Pane */}
      <div className={`flex-1 flex flex-col min-w-0 ${mainMargin} transition-all duration-300 print:ml-0 print:w-full print:max-w-none print:block`}>
        
        {/* Desktop & Mobile Header Bar */}
        <header className="sticky top-0 z-40 bg-[#FFFDF9]/95  border-b border-[#E6D5B8]/30 px-2 py-1.5 md:px-6 md:py-3.5 flex justify-between items-center shadow-xs print:hidden">
          {/* Mobile Menu Trigger */}
          <button onClick={() => setSidebarOpen(true)} className="p-1.5 md:p-2 rounded-lg text-[#302621] hover:bg-[#F9F5EC] md:hidden shrink-0 relative z-40">
            <Menu className="w-5 h-5 md:w-5 md:h-5 stroke-[2.5]" />
          </button>

          {/* Left Brand Area (Mobile only) */}
          <Link to="/office/production" className="flex items-center gap-1.5 md:gap-2 md:hidden ml-2 mr-auto relative z-40">
            <div className="w-[18px] h-[18px] md:w-6 md:h-6 flex items-center justify-center">
              <img src="/logo.png" alt="Satala Logo" className="w-full h-full object-contain" />
            </div>
            <div className="flex flex-col justify-center mt-0.5">
              <span className="font-black text-[10px] md:text-xs text-[#302621] leading-none tracking-tight">Satala {getPortalName(location.pathname)}</span>
              <span className="text-[5px] text-[#8C6208] font-bold tracking-[0.1em] uppercase leading-none mt-0.5">DERMATECH ESSENTIAL</span>
            </div>
          </Link>

          <div className="hidden md:block">
            {/* Left Header Greeting / Info can go here */}
          </div>

          {/* Right Clock and Notification Icon */}
          <div className="flex items-center gap-2.5 md:gap-5 text-[#302621] relative z-40">
            {/* Desktop Clock */}
            <div className="hidden md:flex items-center gap-2 text-xs font-medium text-slate-500 font-mono">
              <Clock className="w-3.5 h-3.5 text-[#8C6208]" />
              <span>{clockTime} - {clockDate}</span>
            </div>

            {/* Mobile Clock */}
            <div className="flex md:hidden items-center gap-1 text-[7px] font-normal text-[#64748B] font-mono text-left leading-[1.2] tracking-tight">
              <Clock className="w-3 h-3 text-[#8C6208] shrink-0 stroke-[1.5]" />
              <div className="flex flex-col">
                <span>{clockTime}</span>
                <span>{clockDate}</span>
              </div>
            </div>
            
            <ClockInButton />
            <NotificationBell />
          </div>
          

        </header>

        {/* Page Content View - Add pb-36 for mobile bottom nav and FABs */}
        <main className="flex-1 p-3 md:p-8 max-w-7xl w-full mx-auto print:p-0 print:max-w-none print:w-full print:block pb-36 md:pb-8">
          <Outlet />
        </main>
      </div>

      {/* Mobile Bottom Navigation */}
      <MobileBottomNav items={getMobileNavItems(location.pathname)} />

    </div>

    {/* Trash Bin Sidebar */}
    <TrashBin
      isOpen={showTrash}
      onClose={() => setShowTrash(false)}
      title={
        location.pathname.startsWith('/office/workspace') ? 'Sampah Workspace' :
        location.pathname.startsWith('/office/finance') ? 'Sampah Finance' :
        location.pathname.startsWith('/office/purchasing') ? 'Sampah Purchasing' :
        'Sampah Produksi'
      }
      entities={
        location.pathname.startsWith('/office/workspace')
          ? [
              { key: 'TeamMember', label: 'Anggota Tim', getDisplayTitle: (item) => item.full_name || item.id },
              { key: 'ClientAccount', label: 'Akun Klien', getDisplayTitle: (item) => item.company_name || item.brand_name || item.id },
              { key: 'RacikanDatabase', label: 'Database Formula', getDisplayTitle: (item) => `${item.nama_brand || ''} — ${item.nama_varian || item.nama_racikan || item.id}` },
              { key: 'DatabaseBahan', label: 'Database Kemasan', getDisplayTitle: (item) => `${item.nama_brand || ''} — ${item.nama_varian || item.nama || item.id}` },
            ]
          : location.pathname.startsWith('/office/finance')
          ? [
              { key: 'ClientPO', label: 'Invoice / PO', getDisplayTitle: (item) => `${item.id} — ${item.customerName || item.brand_name || 'Klien'}` },
              { key: 'AccountMutation', label: 'Mutasi Rekening', getDisplayTitle: (item) => `${item.type === 'masuk' ? '↗ Masuk' : '↘ Keluar'} Rp ${new Intl.NumberFormat('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(item.amount || 0)} — ${item.description || item.id}` },
              { key: 'OperationalExpense', label: 'Biaya Operasional', getDisplayTitle: (item) => `${item.category || ''} — ${item.description || item.id}` },
              { key: 'Payroll', label: 'Rekap Gaji', getDisplayTitle: (item) => `${item.memberName || ''} — ${item.month || item.id}` },
              { key: 'PurchasingRequest', label: 'Pengajuan Pembelian', getDisplayTitle: (item) => `Pengajuan ${item.item_name || ''} — Rp ${new Intl.NumberFormat('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(item.total_biaya || 0)}` },
              { key: 'RiwayatProduksi', label: 'Riwayat Produksi', getDisplayTitle: (item) => `${item.nama_brand || ''} — ${item.tanggal || item.id}` },
            ]
          : location.pathname.startsWith('/office/marketing')
          ? [
              { key: 'ClientAccount', label: 'Akun Klien', getDisplayTitle: (item) => item.company_name || item.brand_name || item.id },
              { key: 'ClientPO', label: 'Invoice / PO', getDisplayTitle: (item) => `${item.id} — ${item.customerName || item.brand_name || 'Klien'}` },
              { key: 'RacikanDatabase', label: 'Database Formula', getDisplayTitle: (item) => `${item.nama_brand || ''} — ${item.nama_varian || item.nama_racikan || item.id}` },
              { key: 'DatabaseBahan', label: 'Database Kemasan', getDisplayTitle: (item) => `${item.nama_brand || ''} — ${item.nama_varian || item.nama || item.id}` },
              { key: 'SampleRacikan', label: 'Sample Racikan', getDisplayTitle: (item) => `${item.nama_brand || ''} — ${item.nama_produk || item.id}` },
            ]
          : location.pathname.startsWith('/office/purchasing')
          ? [
              { key: 'SupplierDatabase', label: 'Database Supplier', getDisplayTitle: (item) => item.nama_supplier || item.id },
              { key: 'PurchasingItem', label: 'Item Purchasing', getDisplayTitle: (item) => `Item #${item.id} — ${item.item_name || 'Bahan'}` },
              { key: 'InventoryItem', label: 'Item Inventori', getDisplayTitle: (item) => `Item #${item.id} — ${item.item_name || 'Bahan'}` },
              { key: 'StockItem', label: 'Item Stok', getDisplayTitle: (item) => `Item #${item.id} — ${item.name || 'Bahan'}` },
              { key: 'PurchasingRequest', label: 'Pengajuan Pembelian', getDisplayTitle: (item) => `Pengajuan ${item.item_name || ''} — Rp ${new Intl.NumberFormat('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(item.total_biaya || 0)}` },
              { key: 'BarangKeluar', label: 'Surat Jalan', getDisplayTitle: (item) => `SJ ${item.nomor_referensi || item.id} — ${item.tujuan || ''}` },
            ]
          : [
              { key: 'ClientPO', label: 'PO Produksi', getDisplayTitle: (item) => `${item.id} — ${item.customerName || item.brand_name || 'Klien'}` },
              { key: 'RacikanDatabase', label: 'Database Formula', getDisplayTitle: (item) => `${item.nama_brand || ''} — ${item.nama_varian || item.nama_racikan || item.id}` },
              { key: 'DatabaseBahan', label: 'Database Kemasan', getDisplayTitle: (item) => `${item.nama_brand || ''} — ${item.nama_varian || item.nama || item.id}` },
              { key: 'RiwayatProduksi', label: 'Riwayat Produksi', getDisplayTitle: (item) => `${item.nama_brand || ''} — ${item.tanggal || item.id}` },
              { key: 'ProductionOrder', label: 'Production Order', getDisplayTitle: (item) => `${item.brand_name || ''} — ${item.id}` },
              { key: 'ReturnOrder', label: 'Return Order', getDisplayTitle: (item) => `${item.brand_name || ''} — ${item.id}` },
              { key: 'DailyProduction', label: 'Produksi Harian', getDisplayTitle: (item) => `Produksi Harian — ${item.id}` },
              { key: 'CatatanHarian', label: 'Catatan Harian', getDisplayTitle: (item) => `Catatan — ${item.id}` },
              { key: 'DeliveryItem', label: 'Item Delivery', getDisplayTitle: (item) => `Delivery — ${item.id}` },
              { key: 'BarangKeluar', label: 'Surat Jalan', getDisplayTitle: (item) => `SJ ${item.nomor_referensi || item.id} — ${item.tujuan || ''}` },
              { key: 'BibitSample', label: 'Bibit Sample', getDisplayTitle: (item) => item.nama_bibit || item.id },
              { key: 'SampleRacikan', label: 'Sample Racikan', getDisplayTitle: (item) => `${item.nama_brand || ''} — ${item.nama_produk || item.id}` },
              { key: 'Botol', label: 'Stok Botol', getDisplayTitle: (item) => item.nama || item.id },
              { key: 'Tutup', label: 'Stok Tutup', getDisplayTitle: (item) => item.nama || item.id },
              { key: 'Spray', label: 'Stok Spray', getDisplayTitle: (item) => item.nama || item.id },
              { key: 'Fullset', label: 'Stok Fullset', getDisplayTitle: (item) => item.nama || item.id },
              { key: 'BahanCair', label: 'Stok Bahan Baku', getDisplayTitle: (item) => item.nama || item.id },
              { key: 'ItemTambahan', label: 'Item Tambahan', getDisplayTitle: (item) => item.nama || item.id },
              { key: 'Legalitas', label: 'Legalitas HPP', getDisplayTitle: (item) => item.nama || item.id },
              { key: 'Jasa', label: 'Jasa HPP', getDisplayTitle: (item) => item.nama || item.id },
            ]
      }
    />
    <AsaNotificationManager />
    <AutoPOCompleter />
    <LateAbsencePopup />
    <TestingModeBanner />
    <VersionUpdatePrompt />
    </BirthdayProvider>
  );
}